/**
 * Game Plan Operations
 * 
 * Provides CRUD operations for game plans and user profiles
 * with proper offline handling, conflict detection, and sync queue management.
 */

import { BaseOperations } from './shared';
import { STORES } from '../schema';
import type { 
  GamePlanOffline, 
  UserProfileOffline 
} from '../schema';

/**
 * Game Plans Operations
 */
export class GamePlanOperations extends BaseOperations<GamePlanOffline> {
  constructor() {
    super(STORES.GAME_PLANS);
  }

  /**
   * Get game plans for a specific user (non-templates)
   */
  async getByUserId(userId: string): Promise<GamePlanOffline[]> {
    try {
      const allPlans = await this.queryByIndex('by_user_id', userId);
      const nonTemplates = allPlans.filter(plan => plan.is_template === 0); // Filter by numeric value
      return nonTemplates;
    } catch (error) {
      console.error('Error getting plans by user ID:', error);
      throw error;
    }
  }

  /**
   * Get templates for a specific user
   */
  async getTemplatesByUserId(userId: string): Promise<GamePlanOffline[]> {
    const allPlans = await this.queryByIndex('by_user_id', userId);
    return allPlans.filter(plan => plan.is_template === 1); // Filter by numeric value
  }

  /**
   * Get all templates (user, admin, and public)
   */
  async getAllTemplates(): Promise<GamePlanOffline[]> {
    return this.queryByIndex('by_is_template', 1); // Use 1 for true in IndexedDB
  }

  /**
   * Get admin templates
   */
  async getAdminTemplates(): Promise<GamePlanOffline[]> {
    return this.queryByIndex('by_is_admin_template', 1); // Use 1 for true in IndexedDB
  }

  /**
   * Get public game plans
   */
  async getPublicPlans(): Promise<GamePlanOffline[]> {
    return this.queryByIndex('by_is_public', 1); // Use 1 for true in IndexedDB
  }

  /**
   * Get public templates
   */
  async getPublicTemplates(): Promise<GamePlanOffline[]> {
    const publicPlans = await this.queryByIndex('by_is_public', 1); // Use 1 for true in IndexedDB
    return publicPlans.filter(plan => plan.is_template === 1); // Filter by numeric value
  }
}

/**
 * User Profile Operations
 */
export class UserProfileOperations extends BaseOperations<UserProfileOffline> {
  constructor() {
    super(STORES.USER_PROFILE);
  }

  /**
   * Get current user profile by user ID
   */
  async getCurrentProfile(userId?: string): Promise<UserProfileOffline | null> {
    if (userId) {
      // Filter by user ID for security
      return this.get(userId);
    }

    // Fallback to first profile (legacy behavior, but should be avoided)
    console.warn('⚠️ getCurrentProfile called without userId - this may cause data leakage');
    const profiles = await this.getAll();
    return profiles[0] || null;
  }

  /**
   * Update current user profile
   */
  async updateCurrentProfile(updates: Partial<UserProfileOffline>, userId?: string): Promise<UserProfileOffline | null> {
    const current = await this.getCurrentProfile(userId);
    if (!current) return null;

    return this.update(current.id, updates);
  }
}

// Export singleton instances
export const gamePlanOps = new GamePlanOperations();
export const userProfileOps = new UserProfileOperations();
